import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useLantern, useSession } from "../hooks";
import { sortedMessages } from "../sync/store";
import { rows } from "../timeline";
import { CatchUp } from "./CatchUp";
import { Composer } from "./Composer";
import { ChannelBar } from "./Bookmarks";
import { MessageItem } from "./MessageItem";
import { Pending } from "./Pending";
import { IconChevron, IconDown, IconPeople, IconPin, IconStar } from "./icons";
import { CHANNELS, sectionOf, STARRED, moveChannel } from "../sidebar";
import { ChannelDetails, type DetailsTab } from "./ChannelDetails";
import { list as listOf, t, tn } from "../i18n";
import { HuddleButton } from "./Huddle";

// Within this many pixels of the bottom, new messages scroll into view; any
// further up, someone is reading back and a button offers the new ones.
const nearBottomPx = 120;
// Scrolled this near the top, earlier messages are shown.
const nearTopPx = 400;
// How many rows a channel shows at first, and adds each time.
const windowRows = 150;

export function ChannelView({ id, highlight, onThread }: { id: string; highlight?: string; onThread: (root: string) => void }) {
  const session = useSession();
  const state = useLantern();
  const cs = state.channels.get(id);
  const list = useRef<HTMLDivElement>(null);
  const atBottom = useRef(true);
  const [behind, setBehind] = useState(false);
  const [members, setMembers] = useState<number | null>(null);
  const [details, setDetails] = useState<DetailsTab | null>(null);
  const countMembers = (live = () => true) =>
    session.api.members(id).then(
      (r) => live() && setMembers(r.members.length),
      () => {},
    );
  const me = state.me?.id;
  const messages = cs ? sortedMessages(cs) : [];
  const pending = [...state.pending.values()].filter((p) => p.channelID === id && !p.threadRootID);
  const now = Date.now();
  const typing = [...(state.typing.get(id) ?? new Map<string, number>()).entries()]
    .filter(([, until]) => until > now)
    .map(([who]) => session.name(who));

  // Where one's reading had got to when the channel was opened: the "New"
  // line goes there, and stays while the channel is shown.
  const lastRead = useMemo(() => {
    const m = state.channels.get(id)?.channel.membership;
    return m && m.unread_count > 0 ? m.last_read_seq : null;
  }, [id]);
  const laidOut = rows(messages, lastRead, me);

  // A long channel renders a window of its newest rows: scrolling up shows
  // more, then loads older history; at the bottom the window shrinks back.
  // A message linked to is always inside it.
  const [shown, setShown] = useState(windowRows);
  const [loadingOlder, setLoadingOlder] = useState(false);
  useEffect(() => setShown(windowRows), [id]);
  const linked = highlight ? laidOut.findIndex((r) => r.kind === "message" && r.m.id === highlight) : -1;
  const start = Math.max(0, Math.min(laidOut.length - shown, linked >= 0 ? linked - 20 : laidOut.length));
  const visible = start > 0 ? laidOut.slice(start) : laidOut;

  // Rows added above keep what is on screen in place: the first message
  // shown stays where it was.
  const anchor = useRef<{ id: string; top: number } | null>(null);
  const trimmed = useRef(false);
  function holdPlace() {
    const first = list.current?.querySelector<HTMLElement>(".message[id]");
    if (first) anchor.current = { id: first.id, top: first.getBoundingClientRect().top };
  }
  useLayoutEffect(() => {
    const a = anchor.current;
    if (trimmed.current && list.current) list.current.scrollTop = list.current.scrollHeight;
    trimmed.current = false;
    if (!a || !list.current) return;
    anchor.current = null;
    const el = document.getElementById(a.id);
    if (el) list.current.scrollTop += el.getBoundingClientRect().top - a.top;
  }, [start, messages.length]);
  async function showEarlier() {
    if (start > 0) {
      holdPlace();
      setShown((n) => n + windowRows);
      return;
    }
    if (!cs?.hasMoreHistory || loadingOlder) return;
    setLoadingOlder(true);
    holdPlace();
    try {
      await session.engine.loadOlder(id);
      setShown((n) => n + 50);
    } finally {
      setLoadingOlder(false);
    }
  }

  useEffect(() => {
    void session.engine.openChannel(id);
    atBottom.current = true;
    setBehind(false);
    setMembers(null);
    setDetails(null);
    let live = true;
    countMembers(() => live);
    void session.loadPins(id).catch(() => {});
    return () => {
      live = false;
      session.engine.closeChannel(id);
    };
  }, [id, session]);

  // Keep the newest message in view unless the person is reading back;
  // their own messages always come into view.
  const newest = messages.at(-1);
  useLayoutEffect(() => {
    if (highlight) return;
    const el = list.current;
    if (!el) return;
    if (atBottom.current || newest?.author.id === me || pending.length > 0) {
      el.scrollTo({ top: el.scrollHeight });
      setBehind(false);
      if (shown > 2 * windowRows) setShown(windowRows);
    } else setBehind(true);
  }, [newest?.seq, pending.length, highlight, id]);
  useEffect(() => {
    if (cs?.loaded && document.visibilityState === "visible") void session.engine.markRead(id, true);
  }, [id, cs?.loaded, cs?.lastSeq, session]);
  // Someone joining or leaving, as an agent added from the composer, changes
  // the count.
  const memberChanges = cs?.memberChanges ?? 0;
  useEffect(() => {
    if (!memberChanges) return;
    let live = true;
    countMembers(() => live);
    return () => {
      live = false;
    };
  }, [id, memberChanges, session]);
  // Opening a closed DM puts it back in the sidebar, once each time.
  const opened = useRef<string>(undefined);
  useEffect(() => {
    if (!cs || opened.current === id) return;
    opened.current = id;
    if (cs.channel.membership?.closed_at) void session.setClosed(id, false).catch(() => {});
  }, [id, cs, session]);
  // A channel not known yet, as one just created, is asked for before it is
  // said not to be available.
  const known = !!cs;
  const [lookedUp, setLookedUp] = useState<string | null>(null);
  useEffect(() => {
    if (known) return;
    let live = true;
    void session.engine.lookUp(id).finally(() => live && setLookedUp(id));
    return () => {
      live = false;
    };
  }, [id, known, session]);
  // Marked unread here, a channel stays so until one leaves it.
  useEffect(() => () => session.engine.release(id), [id, session]);
  useEffect(() => {
    if (highlight) document.getElementById(`m-${highlight}`)?.scrollIntoView({ block: "center" });
  }, [highlight, messages.length]);
  // A message linked to that is older than what is loaded is loaded back to.
  useEffect(() => {
    if (highlight && cs?.loaded && !cs.messages.has(highlight)) void session.engine.loadTo(id, highlight).catch(() => {});
  }, [highlight, id, cs?.loaded, session]);

  function onScroll() {
    // A view on its way out, behind a page that is loading, can still scroll.
    const el = list.current;
    if (!el) return;
    atBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < nearBottomPx;
    if (atBottom.current) setBehind(false);
    if (atBottom.current && shown > 2 * windowRows && !highlight) {
      trimmed.current = true;
      setShown(windowRows);
    }
    if (el.scrollTop < nearTopPx && cs?.loaded && (start > 0 || cs.hasMoreHistory) && !anchor.current) void showEarlier();
  }
  function toBottom() {
    list.current?.scrollTo({ top: list.current.scrollHeight, behavior: "smooth" });
    setBehind(false);
  }

  if (!cs) return <section className="channel-view empty">{lookedUp === id ? t("This channel is not available.") : t("Loading…")}</section>;
  const name = session.channelName(id);
  const dm = cs.channel.kind === "dm" || cs.channel.kind === "group_dm";
  const starred = sectionOf(state.sidebar, id) === STARRED;
  return (
    <section className="channel-view" aria-label={name}>
      <header className="channel-header">
        <div className="channel-title">
          <div className="title-row">
            <h1 className="channel-name" onClick={() => setDetails("about")} title={t("Channel details")}>
              {cs.channel.kind === "private" && (
                <span className="lock" aria-label={t("Private")}>
                  🔒{" "}
                </span>
              )}
              <span className="name-text">{name}</span>
              <button className="details-button" onClick={() => setDetails("about")} aria-label={t("Channel details")}>
                <IconChevron size={14} />
              </button>
            </h1>
            <button
              className={`star-button ${starred ? "on" : ""}`}
              onClick={() => session.arrangeSidebar((x) => moveChannel(x, id, starred ? CHANNELS : STARRED))}
              aria-pressed={starred}
              aria-label={starred ? t("Unstar channel") : t("Star channel")}
              title={starred ? t("Unstar") : t("Star: keep it at the top of your sidebar")}
            >
              <IconStar size={16} filled={starred} />
            </button>
          </div>
          {cs.channel.topic && <p className="topic">{cs.channel.topic}</p>}
        </div>
        <div className="channel-tools">
          {members !== null && !dm && (
            <button className="member-count" onClick={() => setDetails("members")} title={tn(members, "{n} member", "{n} members")} aria-label={t("Members: {n}", { n: members })}>
              <IconPeople size={16} />
              {members}
            </button>
          )}
          {(state.pins.get(id)?.size ?? 0) > 0 && (
            <button className="member-count" onClick={() => setDetails("pinned")} title={t("Pinned messages")} aria-label={t("Pinned: {n}", { n: state.pins.get(id)!.size })}>
              <IconPin size={15} />
              {state.pins.get(id)!.size}
            </button>
          )}
          <HuddleButton channel={id} />
          <CatchUp key={id} channel={id} />
        </div>
        <ChannelBar channel={id} canEdit={state.me?.role !== "guest" && !cs.channel.archived_at} />
      </header>
      <div className="messages-wrap">
      <div className="messages" ref={list} onScroll={onScroll}>
        {cs.loaded && (start > 0 || cs.hasMoreHistory) && (
          <button className="older" onClick={() => void showEarlier()} disabled={loadingOlder}>
            {loadingOlder ? t("Loading…") : start > 0 ? t("Show earlier messages") : t("Load older messages")}
          </button>
        )}
        {!cs.loaded &&
          (cs.failed ? (
            <p className="loading" role="alert">
              {t("Could not load the messages.")}{" "}
              <button className="link" onClick={() => void session.engine.retryHistory(id)}>
                {t("Try again")}
              </button>
            </p>
          ) : (
            <p className="loading">{t("Loading…")}</p>
          ))}
        {cs.loaded && !cs.hasMoreHistory && start === 0 && (
          <div className="channel-start">
            <h2>
              {cs.channel.membership?.cleared_seq
                ? t("You cleared the earlier history of your conversation with {name}.", { name })
                : dm
                  ? t("This is the start of your conversation with {name}.", { name })
                  : t("This is the very beginning of {name}.", { name })}
            </h2>
            {cs.channel.topic && !dm && <p>{cs.channel.topic}</p>}
            {cs.channel.description && !dm && <p className="channel-description">{cs.channel.description}</p>}
          </div>
        )}
        {visible.map((r) =>
          r.kind === "day" ? (
            <div key={r.key} className="day-divider" role="separator">
              <span>{r.label}</span>
            </div>
          ) : r.kind === "new" ? (
            <div key={r.key} className="new-divider" role="separator" aria-label={t("New messages")}>
              <span>{t("New")}</span>
            </div>
          ) : (
            <MessageItem key={r.key} m={r.m} compact={r.compact} onThread={() => onThread(r.m.thread_root_id ?? r.m.id)} highlight={r.m.id === highlight} />
          ),
        )}
        <Pending items={pending} />
        {(state.ephemeral.get(id) ?? []).map((m) => (
          <div key={m.id} className="ephemeral-message">
            <MessageItem m={m} />
            <p className="ephemeral-note">
              {t("Only visible to you")}{" "}
              <button
                className="link"
                onClick={() => session.store.update((s) => s.ephemeral.set(id, (s.ephemeral.get(id) ?? []).filter((x) => x.id !== m.id)))}
              >
                {t("Dismiss")}
              </button>
            </p>
          </div>
        ))}
      </div>
      {behind && (
        <button className="jump" onClick={toBottom}>
          <IconDown size={15} /> {t("New messages")}
        </button>
      )}
      </div>
      <p className="typing" aria-live="polite">
        {typing.length > 0 && (
          <>
            <span className="typing-dots" aria-hidden>
              <i />
              <i />
              <i />
            </span>
            {typing.length > 2 ? t("Several people are typing…") : tn(typing.length, "{who} is typing…", "{who} are typing…", { who: listOf(typing) })}
          </>
        )}
      </p>
      {cs.channel.archived_at ? (
        <div className="archived-bar">
          {t("You are viewing an archived channel; nothing more can be posted to it.")}{" "}
          <button className="link" onClick={() => setDetails("about")}>
            {t("Details")}
          </button>
        </div>
      ) : (
        <Composer key={id} channel={id} placeholder={t("Message {name}", { name })} />
      )}
      {details && (
        <ChannelDetails
          id={id}
          tab={details}
          onClose={() => {
            setDetails(null);
            // Leaving the channel closes this too; there is nothing to count.
            if (session.store.getSnapshot().channels.has(id)) void countMembers();
          }}
        />
      )}
    </section>
  );
}
